import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ArticleCard, useQuestionArticleCardProps } from '@app/components/ArticleCard';
import {
  selectHasFiber,
  selectLocale,
  selectPlanCharacteristics,
  selectStreamOnPromo,
  selectUserInfo,
} from '@app/redux';

import { AccountType, Locale } from '@mfe/shared/schema-types';

import Illustration1 from './assets/Illustration1';
import IllustrationBrazilPix from './assets/IllustrationBrazilPix';
import AddOnSection from './components/AddOnSection';
import { BrazilServiceCard } from './components/BrazilServiceCard';
import { articleQuestionsBrazil } from './components/InternationalArticleCards';
import { ItalyServiceCard } from './components/ItalyServiceCard';
import { isUnleashedFamily } from '../../utils';

import styles from './styles.module.scss';

const StreamOnPromo = React.lazy(() => import('../Stream/pages/StreamOnPromo'));

type ServiceCardLocale = Locale.PtBr | Locale.ItIt;

const useSMBArticle = () => {
  const { t } = useTranslation('Overview');

  return useQuestionArticleCardProps({
    title: t('QuestionSMB.1.title'),
    caption: t('QuestionSMB.1.caption'),
    websiteLink: t('QuestionSMB.1.websiteLink'),
    linkText: t('QuestionSMB.1.linkText'),
  });
};

const useResidentialArticle = () => {
  const { t } = useTranslation('Overview');

  return useQuestionArticleCardProps({
    title: t('Question.1.title'),
    caption: t('Question.1.caption'),
    websiteLink: t('Question.1.link'),
    linkText: t('readArticle'),
  });
};

const useBrArticle = () => {
  const { t } = useTranslation('Overview');

  return useQuestionArticleCardProps({
    title: t(articleQuestionsBrazil[0].title),
    caption: t(articleQuestionsBrazil[0].caption),
  });
};

const useFiberArticle = () => {
  const { t } = useTranslation('Overview');

  return useQuestionArticleCardProps({
    title: t('FiberQuestion.0.title'),
    caption: t('FiberQuestion.0.caption'),
    websiteLink: t('FiberQuestion.0.websiteLink'),
    linkText: t('FiberQuestion.0.linkText'),
  });
};

export const OverviewArticlesSection = () => {
  const {
    locale: { userLocale },
  } = useSelector(selectLocale);
  const hasFiberInternet = useSelector(selectHasFiber);

  const {
    streamEligibility: { isEligible },
    didOrderSucceed: streamOrderSucceeded,
  } = useSelector(selectStreamOnPromo);

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const {
    characteristics: { productFamily, name },
    loading,
  } = useSelector(selectPlanCharacteristics);

  const showStreamPromo = !loading && !streamOrderSucceeded && isEligible && !isUnleashedFamily(productFamily, name);

  const isUS = userLocale === Locale.EnUs;
  const isBrazil = userLocale === Locale.PtBr;
  const isSMB = accountType === AccountType.Smb;

  const serviceCards: Record<ServiceCardLocale, JSX.Element> = {
    [Locale.PtBr]: <BrazilServiceCard />,
    [Locale.ItIt]: <ItalyServiceCard />,
  };

  const smbArticleQuestion = useSMBArticle();
  const residentialArticleQuestion = useResidentialArticle();
  const brArticle = useBrArticle();
  const fiberArticle = useFiberArticle();

  if (hasFiberInternet) {
    return (
      <>
        <div className={styles['articleMargin']}>
          <ArticleCard {...fiberArticle} image={<Illustration1 />} />
        </div>
        <div className={styles['addOnMargin']}>
          <AddOnSection />
        </div>
      </>
    );
  }

  return isUS ? (
    <>
      <div className={styles['articleMargin']}>
        {isSMB ? (
          <ArticleCard {...smbArticleQuestion} image={<Illustration1 />} />
        ) : (
          <ArticleCard {...residentialArticleQuestion} image={<Illustration1 />} />
        )}
      </div>
      {showStreamPromo && (
        <React.Suspense fallback={null}>
          <div className={styles['streamOnMargin']}>
            <StreamOnPromo />
          </div>
        </React.Suspense>
      )}
      <div className={styles['addOnMargin']}>
        <AddOnSection />
      </div>
    </>
  ) : (
    <div className={styles['serviceCards']}>
      <div>{serviceCards[userLocale as ServiceCardLocale]}</div>
      {isBrazil && (
        <div className={styles['articleMargin']}>
          <ArticleCard {...brArticle} image={<IllustrationBrazilPix />} />
        </div>
      )}
    </div>
  );
};
